<template>
    <h2 class="mb-2">Regular dialog</h2>
    <fieldset class="mb-6 grid grid-cols-2 gap-4 rounded-sm border p-4">
        <legend>DsDialog options</legend>
        <label class="col-span-2 grid gap-1">
            Title
            <input
                v-if="useLongTitle"
                v-model="longTitle"
            />
            <input
                v-else
                v-model="title"
            />
            <DsCheckbox
                v-model="useLongTitle"
                label="Use a long title"
                name="closeButton"
                value="true"
            />
        </label>
        <label class="grid gap-1">
            Primary button text
            <input v-model="primaryAction" />
        </label>
        <label class="grid gap-1">
            Secondary button text
            <input v-model="secondaryAction" />
        </label>
        <div>
            <DsCheckbox
                v-model="destructive"
                label="Make primary action destructive"
                name="destructive"
                value="true"
            />
        </div>
        <div class="flex gap-3">
            <DsRadio
                v-model="slotted"
                label="Show slotted content"
                name="slotted"
                value="normal"
            />
            <DsRadio
                v-model="slotted"
                label="Show LONG slotted content"
                name="slotted"
                value="long"
            />
        </div>
        <div>
            <DsCheckbox
                v-model="showCloseButton"
                label="Show close button"
                name="closeButton"
                value="true"
            />
        </div>
        <div class="col-span-2">
            Last action: <span class="inline-block h-4 min-w-8 text-ds-red-600 underline">{{ lastAction }}</span>
        </div>
    </fieldset>

    <DsButton
        size="md"
        variant="primary"
        @click="open = true"
    >
        Open dialog
    </DsButton>

    <DsDialog
        v-model:open="open"
        :is-primary-destructive="!!destructive"
        :primaryAction
        :secondaryAction
        :showCloseButton
        :title="useLongTitle ? longTitle : title"
        @clickPrimary="onPrimary"
        @clickSecondary="onSecondary"
        @close="console.log('CLOSE', $event)"
        @closeAnimationEnd="console.log('CLOSE ANIMATION END', $event)"
    >
        <div
            v-if="slotted === 'normal'"
            class="border-4 border-dotted border-ds-red-500 bg-ds-red-200 py-6"
        >
            🐉 Here be dragons (and slotted content)
        </div>
        <template v-else-if="slotted === 'long'">
            <div
                v-for="(_, key) in Array.from('1'.repeat(10))"
                :key
                class="mb-4"
            >
                <h3>Section {{ key + 1 }}</h3>
                <DsCard>
                    {{ 'Lorem ipsum dolor sit amet. '.repeat(20) }}
                </DsCard>
                <DsButton
                    href="https://www.youtube.com/shorts/Eze2OVNJRyQ?t=12&feature=share"
                    size="md"
                    target="new"
                    variant="primary"
                >
                    Summon dragon 🪄🐉
                </DsButton>
            </div>
        </template>
    </DsDialog>
</template>

<script setup lang="ts">
import DsCard from '@/components/design-system/DsCard.vue';
import DsRadio from '@/components/design-system/form/input/DsRadio.vue';
import DsDialog from '@ds/DsDialog.vue';
import DsButton from '@ds/form/input/DsButton.vue';
import DsCheckbox from '@ds/form/input/DsCheckbox.vue';
import { ref, watch } from 'vue';

const open = ref(false);
const title = ref('This is the title of the dialog');
const longTitle = ref(
    'This is the title of the dialog that containsanemailaddresswithareallylongname@closinglock.com which also includes some other text to see how the dialog handles it.',
);
const useLongTitle = ref(false);
const primaryAction = ref('Primary action');
const secondaryAction = ref('Secondary action');
const destructive = ref();
const slotted = ref<'normal' | 'long'>('normal');
const lastAction = ref('');
const showCloseButton = ref(true);

const onPrimary = () => {
    open.value = false;
    lastAction.value = 'Primary action';
};

const onSecondary = () => {
    open.value = false;
    lastAction.value = 'Secondary action';
};

watch(lastAction, () => {
    setTimeout(() => (lastAction.value = ''), 1500);
});
</script>

<style scoped lang="scss">
label {
    color: gray;

    input {
        @apply rounded-sm border p-1 text-content-primary;
    }
}
</style>
